
import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { connect } from "react-redux";

import {
  ClearRounded,
  VisibilityRounded,
} from "@mui/icons-material";

import {
  Box,
  Button,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";

import { Alert, Dialog, Table } from "../../layout";

import ViewData from "./ViewData";
import ReportPrintButton from "../reports/ReportPrintButton";

import * as actions from "../../api/actions/pos";
import { controller, endpoints } from "../../api/actions/api";

import { AppContext } from "../../App";
import { formatCurrency } from "../../hooks/Currency";

//------------------------------------------------
// Stock Transfers
//------------------------------------------------
const StockTransfers = (props) => {
  const { t } = useTranslation();

  const {
    _clear,
    _getById,
    _list,
    _type,
    _delete,
    _store,
  } = props;

  const navigate = useNavigate();
  const { setLoader } = useContext(AppContext);

  const reportRef = useRef(null);

  const [showForm, setShowForm] = useState(false);
  const [viewItems, setViewItems] = useState([]);

  const [alert, setAlert] = useState({
    isOpen: false,
    type: "",
    title: "",
    subTitle: "",
  });

  //------------------------------------------------
  // Load Stock Transfers by Store
  //------------------------------------------------
  useEffect(() => {
    if (_store?.id) {
      setLoader(true);

      _getById(
        endpoints.StockTrans + controller.GetByStore,
        _store.id,
        actions.ActionType.List,
        () => setLoader(false)
      );
    }

    return () => {
      _clear();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_store?.id]);

  //------------------------------------------------
  // Add / Edit Stock Transfer
  //------------------------------------------------
  const handleAddEdit = (val = null) => {
    navigate(
      val?.id
        ? `stock-transfer-form/${val.id}`
        : "stock-transfer-form"
    );
  };

  //------------------------------------------------
  // Stock Transfers Table Data
  //------------------------------------------------
  const tableData = useMemo(() => {
    if (
      _type !== "stockTransfers" ||
      !Array.isArray(_list)
    ) {
      return [];
    }

    return _list.map((obj) => {
      const items = Array.isArray(obj.items)
        ? obj.items
        : [];

      const totalPrice = items.reduce(
        (sum, item) =>
          sum +
          Number(item.qty || 0) *
            Number(item.product?.sellPrice || 0),
        0
      );

      return {
        ...obj,

        from: obj.fromStore?.storeName || "",
        to: obj.toStore?.storeName || "",

        price: formatCurrency(
          totalPrice,
          _store
        ),

        userName: obj.user?.userName || "",
      };
    });
  }, [_list, _type, _store]);

  //------------------------------------------------
  // Delete Stock Transfer
  //------------------------------------------------
  const handleDlt = (id) => {
    setAlert({
      isOpen: true,
      type: "confirmation",
      title: t("areYouSure"),
      subTitle: t("deleteTransferConfirmation"),

      onConfirm: () => {
        setLoader(true);

        const onComplete = (res) => {
          setLoader(false);

          setAlert({
            isOpen: true,
            type:
              res?.status === 200
                ? "success"
                : "error",
            title:
              res?.status === 200
                ? t("successful")
                : t("error"),
            subTitle:
              res?.msg || t("requestFailed"),
          });
        };

        _delete(
          endpoints.StockTrans,
          id,
          actions.ActionType.RmvList,
          onComplete
        );
      },
    });
  };

  //------------------------------------------------
  // View Stock Transfer Items
  //------------------------------------------------
  const handleView = (data) => {
    const original = Array.isArray(_list)
      ? _list.find(
          (item) =>
            String(item.id) === String(data.id)
        )
      : null;

    setViewItems(
      Array.isArray(original?.items)
        ? original.items
        : Array.isArray(data.items)
        ? data.items
        : []
    );

    setShowForm(true);
  };

  //------------------------------------------------
  // Table Columns
  //------------------------------------------------
  const screenColumns = [
    { id: "actions", label: t("actions") },
    { id: "date", label: t("date") },
    { id: "from", label: t("locationFrom") },
    { id: "to", label: t("locationTo") },
    { id: "price", label: t("totalPrice") },
    { id: "userName", label: t("userName") },
  ];

  const printColumns = screenColumns.filter(
    (column) => column.id !== "actions"
  );

  //------------------------------------------------
  // Action Buttons
  //------------------------------------------------
  const ActionComp = (data) => (
    <Box sx={{ display: "flex", gap: 0.5 }}>
      {/* View */}
      <Tooltip arrow title={t("view")}>
        <IconButton
          onClick={() => handleView(data)}
          size="small"
          className="btn-primary mr-5"
        >
          <VisibilityRounded className="fs-18 m-2" />
        </IconButton>
      </Tooltip>

      {/* Delete */}
      <Tooltip arrow title={t("delete")}>
        <IconButton
          onClick={() => handleDlt(data.id)}
          size="small"
          className="btn-error"
        >
          <ClearRounded className="fs-18 m-2" />
        </IconButton>
      </Tooltip>
    </Box>
  );

  //------------------------------------------------
  // Add Transfer Button
  //------------------------------------------------
  const HeaderComp = () => (
    <Button
      onClick={() => handleAddEdit()}
      className="btn-secondary"
      sx={{
        width: { md: "180px", xs: "100%" },
        mb: { xs: "10px", md: "0px" },
        fontSize: "17px",
        padding: "8px",
      }}
    >
      {t("addTransfer")}
    </Button>
  );

  //------------------------------------------------
  // Render
  //------------------------------------------------
  return (
    <>
      {/* Header and Print Preview */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
          mb: 2,
        }}
      >
        <Typography variant="h5">
          {t("stockTransfers")}
        </Typography>

        <ReportPrintButton
          reportRef={reportRef}
          paperSize="Letter"
          reportTitle={t("stockTransfers")}
        />
      </Box>

      {/* Normal Stock Transfers Table */}
      <Table
        ActionComp={ActionComp}
        HeaderComp={HeaderComp}
        tblHead={screenColumns}
        tblData={tableData}
      />

      {/* Printable Stock Transfers List */}
      <Box
        ref={reportRef}
        className="stock-transfers-print"
        sx={{
          position: "absolute",
          left: "-10000px",
          top: 0,
          width: "1000px",
          backgroundColor: "#fff",
          color: "#000",
          p: 2,
        }}
      >
        <Typography
          variant="h5"
          sx={{
            textAlign: "center",
            fontWeight: "bold",
            mb: 2,
          }}
        >
          {t("stockTransfers")}
        </Typography>

        <Table
          printMode={true}
          ActionComp={() => <div />}
          HeaderComp={() => <div />}
          tblHead={printColumns}
          tblData={tableData}
        />
      </Box>

      {/* Alert */}
      <Alert
        alert={alert}
        setAlert={setAlert}
      />

      {/* Existing Stock Transfer Details Dialog */}
      <Dialog
        show={showForm}
        maxWidth="lg"
        onClose={false}
      >
        <ViewData
          setShowForm={setShowForm}
          items={viewItems}
        />
      </Dialog>
    </>
  );
};

const mapStateToProps = (state) => ({
  _list: state.pos.list,
  _type: state.pos.type,
  _store: state.pos.defStore,
});

const mapActionToProps = {
  _getById: actions.getById,
  _delete: actions.Delete,
  _clear: actions.clear,
};

export default connect(
  mapStateToProps,
  mapActionToProps
)(StockTransfers);
